const THEME_KEY = "sms_theme_accent";

export const DEFAULT_ACCENT = "#2563eb";

const clamp = (value) => Math.min(255, Math.max(0, Math.round(value)));

const normalizeHex = (hex) => {
  if (!hex) return null;
  let value = String(hex).trim().replace("#", "");
  if (value.length === 3) value = value.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return `#${value.toLowerCase()}`;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const raw = normalized.slice(1);
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16)
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, "0")).join("")}`;

const mix = (a, b, ratio = 0.5) => {
  const colorA = hexToRgb(a);
  const colorB = hexToRgb(b);
  if (!colorA || !colorB) return DEFAULT_ACCENT;
  const r = colorA.r * (1 - ratio) + colorB.r * ratio;
  const g = colorA.g * (1 - ratio) + colorB.g * ratio;
  const bVal = colorA.b * (1 - ratio) + colorB.b * ratio;
  return rgbToHex({ r, g, b: bVal });
};

const deriveTheme = (accent) => {
  const normalized = normalizeHex(accent) || DEFAULT_ACCENT;
  return {
    accent: normalized,
    navBg: mix(normalized, "#0b1d34", 0.72),
    navBg2: mix(normalized, "#123055", 0.55),
    navTop: mix(normalized, "#f2bc00", 0.45),
    softBg: mix(normalized, "#ffffff", 0.86)
  };
};

export const applyTheme = (accent, { persist = true } = {}) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const theme = deriveTheme(accent);
  root.style.setProperty("--accent", theme.accent);
  root.style.setProperty("--nav-bg", theme.navBg);
  root.style.setProperty("--nav-bg-2", theme.navBg2);
  root.style.setProperty("--nav-top", theme.navTop);
  root.style.setProperty("--theme-soft", theme.softBg);
  if (persist) localStorage.setItem(THEME_KEY, theme.accent);
};

export const applySavedTheme = () => {
  if (typeof window === "undefined") return;
  const saved = localStorage.getItem(THEME_KEY);
  if (saved) applyTheme(saved, { persist: false });
};

export const resetTheme = () => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.removeProperty("--accent");
  root.style.removeProperty("--nav-bg");
  root.style.removeProperty("--nav-bg-2");
  root.style.removeProperty("--nav-top");
  root.style.removeProperty("--theme-soft");
  localStorage.removeItem(THEME_KEY);
};

